import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, Input, Spin, Switch, Button, message } from 'antd';
import { switchProvider, editStaff } from 'api/group';
import { getReviewData } from 'api/ppaOnboarding';
import { isValidUSPhoneV2 } from 'utils/form';
import UsPhoneInput from 'components/form/USPhoneInput';
import { IReviewData } from 'hooks/usePPAOnboardingReviewInfo';
import SwitchedProviderContext from 'pages/ReviewProviderProfilePage/SwitchedProviderContext';
import useUnsavedCards from 'pages/ReviewProviderProfilePage/useUnsavedCards';
import PracticeSetupTab from 'pages/ReviewProviderProfilePage/components/PracticeSetupTab';
import ProfileTab from 'pages/ReviewProviderProfilePage/components/ProfileTab';
import s from './s.module.less';

const TABS = ['Practice setup', 'Profile'];

interface Props {
    open: boolean;
    providerId: number;
    teamMemberId: number;
    isAdmin?: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const EditProviderModal: React.FC<Props> = ({
    open,
    providerId,
    teamMemberId,
    isAdmin: initialIsAdmin = false,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [reviewData, setReviewData] = useState<IReviewData | null>(null);
    const [switchedToken, setSwitchedToken] = useState<string | undefined>(undefined);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [isAdmin, setIsAdmin] = useState(initialIsAdmin);

    const loadProviderData = useCallback(async () => {
        setLoading(true);
        try {
            const tokenRes = await switchProvider(providerId);
            const loginData = tokenRes?.data?.data;
            if (tokenRes?.data?.code !== 200 || !loginData?.token) {
                message.error(tokenRes?.data?.message || 'Failed to switch provider');
                return;
            }
            const { token } = loginData;
            setSwitchedToken(token);

            const res = await getReviewData(token);
            if (res?.data?.code === 200) {
                const { data } = res.data;
                setReviewData(data);
                setFirstName(data?.basicInfo?.firstName || '');
                setLastName(data?.basicInfo?.lastName || '');
                setPhone((data?.basicInfo?.tel || '').replace(/\D/g, ''));
                setPhoneError('');
            } else {
                message.error(res?.data?.message || 'Failed to load provider data');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to load provider data');
        } finally {
            setLoading(false);
        }
    }, [providerId]);

    useEffect(() => {
        if (open) {
            setIsAdmin(initialIsAdmin);
            loadProviderData();
        }
    }, [open, loadProviderData, initialIsAdmin]);

    const refreshProviderData = useCallback(async () => {
        if (!switchedToken) return;
        try {
            const res = await getReviewData(switchedToken);
            if (res?.data?.code === 200) {
                const { data } = res.data;
                setReviewData(data);
                setFirstName(data?.basicInfo?.firstName || '');
                setLastName(data?.basicInfo?.lastName || '');
                setPhone((data?.basicInfo?.tel || '').replace(/\D/g, ''));
                setPhoneError('');
            }
        } catch (error) {
            console.error(error);
        }
    }, [switchedToken]);

    const { onEditingChange, blockIfUnsaved, runIfSaved } = useUnsavedCards();

    const switchedProviderValue = useMemo(
        () => ({
            scheduleToken: switchedToken,
            providerId,
            onRefresh: refreshProviderData,
            bordered: true,
            onEditingChange,
        }),
        [switchedToken, providerId, refreshProviderData, onEditingChange],
    );

    const basicInfo = reviewData?.basicInfo;

    const practiceSetupData = reviewData
        ? {
              firstName: basicInfo?.firstName,
              lastName: basicInfo?.lastName,
              credential: basicInfo?.credential,
              gender: basicInfo?.gender,
              services: reviewData.services,
              availability: reviewData.availability,
              reminderSettings: reviewData.calendarSetting,
          }
        : null;

    const handlePhoneChange = useCallback((val: string) => {
        setPhone(val);
        setPhoneError('');
    }, []);

    const handlePhoneBlur = useCallback(() => {
        if (phone && !isValidUSPhoneV2(phone)) {
            setPhoneError('Please enter a 10-digit phone number');
        }
    }, [phone]);

    const handleSave = async () => {
        // 底部 Save 只保存姓名/电话/admin，卡片里未保存的改动会被丢掉，先拦住
        if (blockIfUnsaved()) return;
        if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
            message.error('Please fill in all required fields');
            return;
        }
        if (!isValidUSPhoneV2(phone)) {
            setPhoneError('Please enter a 10-digit phone number');
            return;
        }
        setSaving(true);
        try {
            const res = await editStaff(teamMemberId, {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phoneNumber: phone.trim(),
                isAdmin,
            });
            if (res?.error) {
                message.error(res.error || 'Failed to save provider info');
                return;
            }
            message.success('Provider updated successfully');
            onSuccess();
        } catch (error) {
            console.error(error);
            message.error('Failed to save provider info');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title="Edit provider"
            open={open}
            onCancel={onClose}
            width={1000}
            className={s.modal}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" onClick={handleSave} loading={saving} disabled={loading}>
                    Save
                </Button>,
            ]}
        >
            {loading || !switchedToken ? (
                <div className={s.loadingArea}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <div className={s.basicInfoCard}>
                        <div className={s.formRow}>
                            <div className={s.inputGroup}>
                                <span className={s.inputLabel}>
                                    First name <span className={s.required}>*</span>
                                </span>
                                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                            </div>
                            <div className={s.inputGroup}>
                                <span className={s.inputLabel}>
                                    Last name <span className={s.required}>*</span>
                                </span>
                                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                            </div>
                        </div>
                        <div className={s.formRow}>
                            <div className={s.inputGroup}>
                                <span className={s.inputLabel}>
                                    Email <span className={s.required}>*</span>
                                </span>
                                <Input value={basicInfo?.email || ''} disabled />
                            </div>
                            <div className={s.inputGroup}>
                                <span className={s.inputLabel}>
                                    Phone number <span className={s.required}>*</span>
                                </span>
                                <UsPhoneInput
                                    value={phone}
                                    onChange={(val) => handlePhoneChange(val as string)}
                                    onBlur={handlePhoneBlur}
                                    status={phoneError ? 'error' : undefined}
                                />
                                {phoneError && <span className={s.fieldError}>{phoneError}</span>}
                            </div>
                        </div>
                        <div className={s.toggleRow}>
                            <Switch checked={isAdmin} onChange={setIsAdmin} />
                            <div className={s.toggleContent}>
                                <div className={s.toggleTitle}>Practice-wide admin access</div>
                                <div className={s.toggleDesc}>
                                    Grants access to system configuration, user management, billing oversight, and
                                    practice-level settings. Overrides role-based permissions.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={s.tabs}>
                        {TABS.map((tab, i) => (
                            <div
                                key={tab}
                                className={`${s.tab} ${i === activeTab ? s.tabActive : ''}`}
                                onClick={() => {
                                    if (i !== activeTab) runIfSaved(() => setActiveTab(i));
                                }}
                            >
                                <span className={s.tabLabel}>{tab}</span>
                                <div className={i === activeTab ? s.tabBar : s.tabBarHidden} />
                            </div>
                        ))}
                    </div>

                    <div className={s.tabContent}>
                        <SwitchedProviderContext.Provider value={switchedProviderValue}>
                            {activeTab === 0 ? (
                                <PracticeSetupTab key={providerId} provider={practiceSetupData} />
                            ) : (
                                <ProfileTab key={providerId} />
                            )}
                        </SwitchedProviderContext.Provider>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default EditProviderModal;
